import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy, Download, Refresh, Search } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { getAllTags } from "modules/tags/services/tagService";
import ConsoleStats from "./ConsoleStats";
import { selectTagRows, useTagsTable } from "../../hooks/useTagsTable";
const columns = [
  ["id", "ID"],
  ["qr", "QR"],
  ["reverse_number", "Reverse Number"],
  ["code", "Código"],
  ["epc", "EPC"],
  ["statusconsole", "Estado consola"],
  ["lot", "Lote"],
  ["userId", "Usuario ID"],
  ["createdAt", "Fecha creación"],
];
const states = [
  "SIN PISTOLEAR",
  "PISTOLEADO",
  "ERROR",
  "PROCESANDO",
  "VALIDADO",
  "VERIFICADO",
  "ACTIVO",
];
const colors = {
  Activado: "success",
  Asignado: "info",
  "Sin asignar": "warning",
  PISTOLEADO: "success",
  ERROR: "error",
};
export default function TableTagsContent({ refreshFlag, onError, lot }) {
  const table = useTagsTable({ refreshFlag, onError, lot });
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState("");
  const exportRequest = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      exportRequest.current?.abort();
    };
  }, []);
  const pendingFilters =
    table.lotFilter.trim() !== table.criteria.lot ||
    table.search.trim() !== table.criteria.search ||
    table.statusFilter !== table.criteria.status;
  const busy = table.loading || exporting || pendingFilters;
  const exportExcel = async () => {
    if (exportRequest.current || busy) return;
    exportRequest.current = new AbortController();
    setExporting(true);
    try {
      const result = await getAllTags({
        lot: table.criteria.lot ? Number(table.criteria.lot) : null,
        status: table.criteria.status === "todos" ? null : table.criteria.status,
        signal: exportRequest.current.signal,
      });
      if (!mounted.current) return;
      const tags = selectTagRows(result.data, table.criteria.search, table.sort);
      if (!tags.length) {
        setMessage("No hay registros para exportar.");
        return;
      }
      const sheet = XLSX.utils.aoa_to_sheet([
        columns.map(([, title]) => title),
        ...tags.map((tag) =>
          columns.map(([key]) =>
            key === "createdAt" ? tag.createdAtFormatted : tag[key] ?? "Sin información"
          )
        ),
      ]);
      sheet["!cols"] = [12, 26, 24, 42, 32, 26, 12, 20, 26].map((wch) => ({ wch }));
      sheet["!autofilter"] = { ref: sheet["!ref"] };
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Tags");
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet([
          ["Filtro", "Valor"],
          ["Lote", table.criteria.lot || "Todos"],
          ["Estado", table.criteria.status],
          ["Búsqueda", table.criteria.search],
          ["Registros", tags.length],
          ["Zona horaria", "America/Bogota"],
        ]),
        "Filtros"
      );
      XLSX.writeFile(
        workbook,
        `tags-${new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Bogota" }).format(
          new Date()
        )}.xlsx`
      );
      setMessage(
        `Se exportaron ${tags.length.toLocaleString("es-CO")} tags, incluyendo todas las páginas.`
      );
    } catch (error) {
      if (mounted.current) setMessage(error.message || "No se pudo exportar el listado completo.");
    } finally {
      exportRequest.current = null;
      if (mounted.current) setExporting(false);
    }
  };
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setMessage("Valor copiado.");
    } catch {
      setMessage("No fue posible copiar el valor.");
    }
  };
  return (
    <Card sx={{ overflow: "hidden", borderRadius: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        gap={2}
        sx={{ p: 2.5, color: "#fff", background: "linear-gradient(135deg,#1e293b,#0b4f6c)" }}
      >
        <Box>
          <Typography variant="h5" color="inherit">
            Lista de Tags
          </Typography>
          <Typography variant="body2" color="inherit">
            {table.loading
              ? "Consultando registros…"
              : table.error
              ? "No se pudo completar la consulta"
              : `${table.total.toLocaleString(
                  "es-CO"
                )} registros · Consulta, búsqueda y exportación`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<Download />}
            onClick={exportExcel}
            disabled={busy || Boolean(table.error) || !table.total}
            sx={{ bgcolor: "#fff", color: "#1e293b", "&:hover": { bgcolor: "#f1f5f9" } }}
          >
            {exporting ? "Exportando…" : "Exportar Excel"}
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={table.refresh}
            disabled={busy}
            sx={{ color: "#fff", border: "1px solid #ffffff88" }}
          >
            Actualizar
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ p: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              label="Buscar en todos los tags"
              placeholder="QR, reverse number, EPC, código…"
              fullWidth
              size="small"
              value={table.search}
              onChange={(event) => table.setSearch(event.target.value)}
              disabled={exporting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Lote"
              value={table.lotFilter}
              disabled={exporting}
              onChange={(event) => table.setLotFilter(event.target.value)}
              inputProps={{ inputMode: "numeric" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              size="small"
              fullWidth
              label="Estado del tag"
              value={table.statusFilter}
              disabled={exporting}
              onChange={(event) => table.setStatusFilter(event.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  minHeight: "40px !important",
                  py: "0 !important",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <MenuItem value="todos">Todos</MenuItem>
              {states.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button onClick={table.clear} disabled={exporting}>
              Limpiar filtros
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ height: 4, overflow: "hidden" }}>{busy && <LinearProgress />}</Box>
      {table.loading && !table.progress && (
        <Typography role="status" variant="caption" sx={{ px: 2.5, py: 1 }} color="text.secondary">
          Esperando respuesta del servidor. La consulta puede tardar hasta 2 minutos.
        </Typography>
      )}
      {table.progress && (
        <Typography variant="caption" role="status" sx={{ px: 2.5, py: 1 }}>
          Consultando todas las páginas: {table.progress.loaded.toLocaleString("es-CO")}
          {table.progress.total
            ? ` de ${table.progress.total.toLocaleString("es-CO")}`
            : " registros"}
          …
        </Typography>
      )}
      <ConsoleStats
        stats={table.consoleStats}
        loading={busy}
        error={Boolean(table.error)}
        total={table.total}
      />
      {table.error ? (
        <Alert severity="error" action={<Button onClick={table.refresh}>Reintentar</Button>}>
          {table.error}
        </Alert>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 650, opacity: busy ? 0.55 : 1 }}>
            <Table
              stickyHeader
              size="small"
              aria-label="Listado de tags"
              sx={{
                "& th": {
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  bgcolor: "background.paper",
                },
                "& td": { fontSize: 14, py: 1.5 },
              }}
            >
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {columns.map(([key, label]) => (
                    <TableCell key={key}>
                      <TableSortLabel
                        active={table.sort.field === key}
                        direction={table.sort.field === key ? table.sort.direction : "asc"}
                        disabled={busy}
                        onClick={() => {
                          table.setSort({
                            field: key,
                            direction:
                              table.sort.field === key && table.sort.direction === "asc"
                                ? "desc"
                                : "asc",
                          });
                          table.setPage(0);
                        }}
                      >
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {table.rows.map((tag, index) => (
                  <TableRow hover key={`${tag.id}-${index}`}>
                    {columns.map(([key]) => (
                      <TableCell key={key}>
                        {key === "statusconsole" ? (
                          <Chip
                            size="small"
                            label={tag.statusconsole}
                            color={colors[tag.statusconsole] || "default"}
                          />
                        ) : key === "createdAt" ? (
                          tag.createdAtFormatted
                        ) : ["qr", "reverse_number", "code", "epc"].includes(key) ? (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Tooltip title={String(tag[key])}>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  maxWidth: key === "code" ? 240 : 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontFamily: "monospace",
                                }}
                              >
                                {tag[key]}
                              </Typography>
                            </Tooltip>
                            <IconButton
                              size="small"
                              aria-label={`Copiar ${key} ${tag.id}`}
                              onClick={() => copy(tag[key])}
                            >
                              <ContentCopy sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        ) : (
                          tag[key] ?? "Sin información"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {!table.rows.length && !table.loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No se encontraron tags con estos filtros.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={table.total}
            page={table.page}
            rowsPerPage={table.pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count.toLocaleString("es-CO")}`
            }
            onPageChange={(_, page) => table.setPage(page)}
            onRowsPerPageChange={(event) => {
              table.setPageSize(Number(event.target.value));
              table.setPage(0);
            }}
            SelectProps={{ disabled: busy }}
            backIconButtonProps={{ disabled: busy || !table.page }}
            nextIconButtonProps={{
              disabled: busy || (table.page + 1) * table.pageSize >= table.total,
            }}
            sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap" } }}
          />
        </>
      )}
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={6000}
        onClose={() => setMessage("")}
        message={message}
      />
    </Card>
  );
}
TableTagsContent.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
  lot: PropTypes.number,
};
TableTagsContent.defaultProps = { refreshFlag: false, onError: undefined, lot: null };
